import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Alert, type AlertColor, Snackbar } from '@mui/material';

type SnackbarContextValue = {
  /**
   * Show a transient toast. `severity` defaults to `'success'` to match the
   * most common call site (an action that just succeeded).
   */
  showSnackbar: (message: string, severity?: AlertColor) => void;
};

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

// Single source of truth for toast behaviour (FE-MNT-07). Replaces the
// per-component `useState<{open,message,severity}>` + `<Snackbar><Alert>`
// boilerplate that had been copy-pasted across 6+ files with auto-hide
// constants drifting between 3000 / 4000 / 5000ms. Anchor is inherited from
// the theme default (`MuiSnackbar.defaultProps` → bottom-center in
// ThemeProvider) so placement stays consistent app-wide.
const AUTO_HIDE_MS = 4000;

type SnackbarState = {
  open: boolean;
  message: string;
  severity: AlertColor;
};

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = useCallback((message: string, severity: AlertColor = 'success') => {
    setState({ open: true, message, severity });
  }, []);

  const handleClose = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  const value = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar open={state.open} autoHideDuration={AUTO_HIDE_MS} onClose={handleClose}>
        <Alert severity={state.severity} onClose={handleClose} sx={{ width: '100%' }}>
          {state.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): SnackbarContextValue {
  const ctx = useContext(SnackbarContext);
  if (!ctx) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return ctx;
}
